import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useClickOutside } from 'services/useClickOutside';
import CountryBadge from './CountryBadge';
import { initializeCountryRegions, getCountrySelectOptionsGrouped, findCountryData } from '../moduleServices/trips_sanitize';

const PANEL_MAX_H = 280;

const getPortalStyle = (buttonEl) => {
  if (!buttonEl) return {};
  const rect = buttonEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const openAbove = spaceBelow < PANEL_MAX_H && rect.top > spaceBelow;
  const left = Math.min(rect.left, window.innerWidth - 288 - 8);
  return {
    position: 'fixed',
    top: openAbove ? `${rect.top - PANEL_MAX_H - 8}px` : `${rect.bottom + 4}px`,
    left: `${Math.max(8, left)}px`,
    width: `${Math.min(288, window.innerWidth - 16)}px`,
    maxHeight: `${PANEL_MAX_H}px`,
    zIndex: 70,
  };
};

/**
 * Country selector: stores ISO3. Trigger shows [flag ISO2] [ISO3] ▼. Panel grouped by region.
 */
const DropdownCountrySelect = ({
  value = '',
  onChange,
  disabled = false,
  placeholder = 'Select country',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await initializeCountryRegions();
      if (!cancelled) {
        setGrouped(getCountrySelectOptionsGrouped());
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useClickOutside([buttonRef, panelRef], () => setIsOpen(false), isOpen);

  const resolved = value ? findCountryData(value, 'iso3') : null;

  const filteredGroups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return grouped;
    return grouped
      .map((g) => ({
        region: g.region,
        countries: g.countries.filter(
          (c) =>
            c.iso3.toLowerCase().includes(q) ||
            (c.name && c.name.toLowerCase().includes(q))
        ),
      }))
      .filter((g) => g.countries.length > 0);
  }, [grouped, filter]);

  const handleSelect = (iso3) => {
    onChange(iso3);
    setIsOpen(false);
    setFilter('');
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled || loading}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled && !loading) setIsOpen((o) => !o);
        }}
        className={`flex items-center gap-2 w-full rounded-lg px-2 py-1.5 text-left text-xs bg-gray-100 hover:bg-gray-200 border-0 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-400 ${className}`}
      >
        {loading ? (
          <span className="text-gray-400">Loading countries…</span>
        ) : (
          <>
            {resolved?.iso2 ? <CountryBadge code={resolved.iso2} size={18} /> : value ? <span className="w-[18px]" /> : null}
            <span className={`flex-1 truncate ${value ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
              {value ? (resolved ? resolved.iso3 : value) : placeholder}
            </span>
            <ChevronDownIcon className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {isOpen &&
        !loading &&
        createPortal(
          <div
            ref={panelRef}
            data-qapp-portal="country-select"
            style={getPortalStyle(buttonRef.current)}
            className="bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col overflow-hidden"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="p-2 border-b border-gray-100 flex-shrink-0">
              <input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by ISO3 or name…"
                className="w-full border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gray-500"
                autoFocus
              />
            </div>
            <div className="overflow-y-auto flex-1 p-1">
              {filteredGroups.length === 0 ? (
                <p className="text-xs text-gray-500 px-2 py-3 text-center">No matches</p>
              ) : (
                filteredGroups.map((g) => (
                  <div key={g.region} className="mb-2 last:mb-0">
                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-2 py-1 sticky top-0 bg-white">
                      {g.region}
                    </div>
                    {g.countries.map((c) => (
                      <button
                        key={c.iso3}
                        type="button"
                        onClick={() => handleSelect(c.iso3)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs hover:bg-gray-100 ${
                          value === c.iso3 ? 'bg-gray-100' : ''
                        }`}
                      >
                        <CountryBadge code={c.iso2} size={18} />
                        <span className="font-medium text-gray-900 w-10 flex-shrink-0">{c.iso3}</span>
                        <span className="text-gray-500 truncate">{c.name}</span>
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default DropdownCountrySelect;
